import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';

interface StrengthCluster {
  strength: string;
  frequency: number;
  departments: string[];
  confidence: 'High' | 'Medium' | 'Low';
  emergingTrend: boolean;
}

interface StrengthClustersSectionProps {
  data: StrengthCluster[];
  expanded?: boolean;
  onViewMore?: () => void;
}

const StrengthClustersSection: React.FC<StrengthClustersSectionProps> = ({ data, expanded = false, onViewMore }) => {
  const { t } = useTranslation();

  const getConfidenceColor = (confidence: string) => {
    if (confidence === 'High') return 'bg-green-100 text-green-700 border-green-200';
    if (confidence === 'Medium') return 'bg-blue-100 text-blue-700 border-blue-200';
    return 'bg-gray-100 text-gray-700 border-border';
  };

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Icon name="Sparkles" size={24} className="mr-2 text-purple-600" />
            {t('Strength Clusters')}
          </h2>
          <p className="text-sm text-gray-600 mt-1">{t('Top recurring strengths across the organization')}</p>
        </div>
        <span className="text-sm text-gray-500">{data?.length} {t('strengths identified')}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data?.slice(0, expanded ? data?.length : 3)?.map((strength, index) => (
          <div
            key={index}
            className="border border-border rounded-lg p-4 hover:border-primary/50 hover:shadow-sm transition-all bg-gradient-to-r from-white to-purple-50/30"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Sparkles" size={18} className="text-purple-600" />
                  <h3 className="text-base font-semibold text-gray-900">{strength?.strength}</h3>
                  {strength?.emergingTrend && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                      <Icon name="TrendingUp" size={10} className="mr-1" />
                      {t('Emerging')}
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-4 text-xs text-gray-500 mb-3">
                  <span className="flex items-center">
                    <Icon name="Users" size={12} className="mr-1" />
                    {strength?.frequency} {t('employees')}
                  </span>
                  <span className="flex items-center">
                    <Icon name="Building2" size={12} className="mr-1" />
                    {strength?.departments?.length} {t('departments')}
                  </span>
                </div>
              </div>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getConfidenceColor(strength?.confidence)}`}>
                {strength?.confidence}
              </span>
            </div>

            {/* Frequency Bar */}
            <div className="mb-3">
              <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                <span>{t('Frequency')}</span>
                <span>{strength?.frequency} {t('occurrences')}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-purple-500 to-indigo-600 h-2 rounded-full"
                  style={{ width: `${(strength?.frequency / 40) * 100}%` }}
                />
              </div>
            </div>

            {/* Departments */}
            <div className="flex flex-wrap gap-1">
              {strength?.departments?.slice(0, 3)?.map((dept, deptIndex) => (
                <span
                  key={deptIndex}
                  className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs"
                >
                  {dept}
                </span>
              ))}
              {strength?.departments?.length > 3 && (
                <span className="px-2 py-0.5 text-xs text-gray-500">
                  +{strength?.departments?.length - 3} {t('more')}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {!expanded && data?.length > 3 && (
        <div className="mt-4 text-center">
          {onViewMore ? (
            <button
              onClick={onViewMore}
              className="text-primary hover:text-primary/80 text-sm font-medium"
            >
              {t('View All')} {data?.length} {t('Strengths')}
            </button>
          ) : (
            <p className="text-sm text-gray-500">{t('Showing')} 3 {t('of')} {data?.length} {t('strengths')}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default StrengthClustersSection;
